import { AnyAction } from 'redux';
import { EditUser, userInformation } from './DataAction';
import { showFeedbackModal } from './UserFeedbackActions';
import { AppThunk } from '../types';
import { Citation } from './ChatAction';
import { ModalState } from '../reducer/modalReducer';
import { UserProps } from '../../components/Settings/AccountSettings/AccountSettings';
import {
  PiBriefcaseLight,
  PiPasswordLight,
  PiTagLight
} from 'react-icons/pi';
import { NoteItem } from '../../components/Matters/tabs/Notes';
import { AuditLogEntry } from './AuditLogActions';
import { title } from 'process';
import { translate } from '../../i18n/translate';

export const showModal = (payload: ModalState): AnyAction => ({
  type: 'SHOW_MODAL',
  payload,
});

export const hideModal = (): AnyAction => ({
  type: 'HIDE_MODAL',
});

export const showCreateFolderModal = (
  handleCreateFolder?: (folderName: string) => void,
  FolderID?: string,
  handleCreateSubFolder?: (folderName: string, parentFolderId: string) => void,
  isSubfolder: boolean = false 
): AppThunk => async (dispatch) => {
  dispatch(
    showModal({
      type: 'create',
      message: isSubfolder ? translate('Create New Subfolder') : translate('Create New Folder'),
      subMsg: isSubfolder
        ? translate('Please enter a name for the subfolder you want to create.')
        : translate('Please enter a name for the folder you want to create.'),
      onConfirm: (folderName?: string | File) => {
        if (typeof folderName === 'string') {
          if (FolderID && handleCreateSubFolder) {
            handleCreateSubFolder(folderName, FolderID);
          } else if (handleCreateFolder) {
            handleCreateFolder(folderName);
          }
        }
      },
      showModal: true,
      width: '30%',
    })
  );
};

export const showPasswordModal = (onConfirm: (password: string) => void): AnyAction => ({
  type: 'SHOW_MODAL',
  payload: {
    type: 'password',
    message: translate('Please enter your passowrd.'),
    subMsg: translate('Confirm this action by inserting your password.'),
    icon: <PiPasswordLight className='w-6 h-6 text-gray-600' />,
    onConfirm: (password?: string | File) => {
      if (password && typeof password === 'string') {
        onConfirm(password);
      }
    },
    showModal: true,
  },
});

export const showPasswordProfileModal = (
  userProps: UserProps,
  userId: string,
  userType: string,
  onSuccess: () => void
) => {
  return (dispatch: any) => {
    dispatch(showModal({
      type: 'password',
      message: translate('Enter your password'),
      subMsg: ('Please confirm your password to update your personal information.'),
      icon: <PiPasswordLight className='w-6 h-6 text-gray-600' />,
      onConfirm: (password?: string | File) => {
        if (password && typeof password === 'string') {
          const formData = new FormData();
          Object.entries(userProps).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
              if (key === 'ProfilePicture' && value instanceof File) {
                formData.append(key, value);
              } else {
                formData.append(key, value.toString());
              }
            }
          });
          formData.append("Password", password);
          formData.append("UserID", userId);
          formData.append("UserType", userType);
          dispatch(EditUser(formData, onSuccess));
          dispatch(userInformation())
        }
      },
      showModal: true,
      width: '35%',
      gap: 10
    }));
  };
};

export const ShowReceiverOptions = () => {

  return (dispatch: any) => {
    dispatch(showModal({
      type: 'receiver-options',
      message: translate('Please select your receiver account'),
      onConfirm: (password: any) => {
      },
      showModal: true
    }));
  };
};

export const ShowPromptFeedback = () => {
  return (dispatch: any) => {
    return new Promise<string>((resolve) => {
      dispatch(showModal({
        type: 'promptfeedback',
        message: translate('Help us get better!'),
        onConfirm: (data?: string | File) => {
          if (typeof data === 'string') {
            resolve(data || '');
            dispatch(showFeedbackModal({
              showModal: true,
              message: translate('Feedback sent'),
              modalType: 'success',
              duration: 3000
            }));
          }
        },
        showModal: true
      }));
    });
  };
};

export const showCitationModal = (citation: Citation) => {
  return {
    type: 'SHOW_MODAL',
    payload: {
      type: 'citation',
      message: '',
      citation: citation,
      showModal: true,
    },
  };
};

export const handleAttachExistingCaseClick = (cliendId: string) => {
  return (dispatch: any) => {
    dispatch(showModal({
      type: 'attachExistingCaseToClient',
      message: translate('Attach a case'),
      subMsg: translate('Please select and attach your client to an existing case.'),
      icon: <PiBriefcaseLight className='w-6 h-6 text-gray-600' />,
      showModal: true,
      clientId: cliendId || '' 
    }));
  };
};

export const handleAISettings = () => {
  return (dispatch: any) => {
    dispatch(showModal({
      type: 'ai-settings',
      message: translate('AI Settings'),
      showModal: true,
    }));
  };
};

export const showEditMatterDataModal = (
  matterId: string,
  category: 'notes' | 'financial_data',
  item?: NoteItem,
  onConfirm?: (data: { label: string; value: string; type: string; oldLabel?: string }) => Promise<void>
) => {
  return (dispatch: any) => {
    const operation: 'add' | 'edit' = item ? 'edit' : 'add';
    dispatch(showModal({
      showModal: true,
      type: 'editMatterData',
      icon: <PiTagLight className='w-6 h-6 text-gray-600' />,
      message: operation === 'add' ? ('Add Data') : ('Edit Data'),
      subMsg: operation === 'add' ? ('Add any type of data you need for this matter.') : ('Edit your existing data.'),
      item,
      category,
      matterId,
      onConfirm: (data: { label: string; value: string; type: string }) => {
        if (onConfirm) {
          onConfirm({ ...data, oldLabel: item?.label });
        }
      },
    }));
  };
};

export const handleAddCollaborator = (caseId: string) => {
  return (dispatch: any) => {
    dispatch(showModal({
      type: 'addCollaborator',
      message: translate('Attach one or more collaborators to your case'),
      showModal: true,
      caseId: caseId
    }));
  };
};

export const showConnectMatterModal = (
  clientId: string,
  clientName: string,
  clientUuid: string,
  matters: any[],
  onMatterConnected: () => void,
  fetchMatters?: (query: string, page: number) => Promise<any[]>,
  totalPages?: number
) => {
  return (dispatch: any) => {
    dispatch(showModal({
      type: 'connectMatter',
      message: translate('Connect Matter'),
      showModal: true,
      clientId,
      clientName,
      clientUuid,
      matters,
      onMatterConnected,
      fetchMatters,
      totalMatterPages: totalPages,
    }));
  };
};

export const showUploadClientDocumentModal = (
  clientUuid: string,
  clientName: string,
  onDocumentUploadComplete?: () => void
) => {
  return (dispatch: any) => {
    dispatch(showModal({
      type: 'uploadClientDocument',
      message: translate('Upload Document'),
      showModal: true,
      clientUuid,
      clientName,
      onDocumentUploadComplete,
    }));
  };
};

const capitalize = (s: string) => s.replace(/\b\w/g, c => c.toUpperCase()).replace(/_/g, ' ');

export const showAuditLogDetailModal = (entry: AuditLogEntry): AnyAction => ({
  type: 'SHOW_MODAL',
  payload: {
    type: 'auditLogDetail',
    message: entry.action ? capitalize(entry.action) : 'Audit Log Detail',
    auditEntry: entry,
    showModal: true,
    subMsg: entry.entity_type ? capitalize(entry.entity_type) : '',
    icon: <PiTagLight className='w-6 h-6 text-gray-600' />
  },
});
